import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";

import { ErrorTypography } from "components/ErrorTypography/ErrorTypography";
import { useShipperNetwork } from "hooks";

import { RouteInformationSection } from "./RouteInformationSection";
import { SectionDisplay } from "./SectionDisplay";
import { formatShipmentData } from "./utils";

import type { CustomerListItem } from "components/SupplierNetwork/types";
import type { CreateShipmentFormState } from "types/shipment";

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  formState: CreateShipmentFormState;
  submitLoading: boolean;
  error: string | null;
  supplierCustomers: CustomerListItem[];
}

export function ShipmentReviewModal(props: Props) {
  const {
    open,
    onClose,
    onConfirm,
    formState,
    submitLoading,
    error,
    supplierCustomers,
  } = props;

  const { followingCarriers } = useShipperNetwork();
  const sections = formatShipmentData(
    formState,
    followingCarriers,
    supplierCustomers,
  );

  const isProject = formState.shipmentType === "MULTI_SHIPMENT_PROJECT";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "80vh",
        },
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5">
          Review {isProject ? "Project" : "Shipment"}
        </Typography>
        <IconButton edge="end" onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, overflowX: "hidden", overflowY: "auto" }}>
        <Typography
          variant="body1"
          color="textSecondary"
          gutterBottom
          sx={{ mb: 3 }}
        >
          Please review the details below and confirm to post your{" "}
          {isProject ? "project" : "shipment"}.
        </Typography>
        {sections.map((section) =>
          section.title === "Route Information" ? (
            <RouteInformationSection
              key={section.title}
              routePoints={formState.routePoints}
            />
          ) : (
            <SectionDisplay key={section.title} section={section} />
          ),
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Stack width="100%">
          {error != null && <ErrorTypography error={error} />}
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={submitLoading}
              size="large"
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={onConfirm}
              disabled={submitLoading}
              size="large"
              startIcon={
                submitLoading ? <CircularProgress size={20} /> : <AddIcon />
              }
            >
              {submitLoading
                ? "Posting..."
                : `Post ${isProject ? "Project" : "Shipment"}`}
            </Button>
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
